import React from 'react';
import { Link } from 'react-router-dom';
import { CardNumberElement, CardCvcElement, CardExpiryElement } from '@stripe/react-stripe-js';
import { Button, Title, Text, InputWrapper, ErrorMessage, Checkbox } from 'components';
import { Routes } from 'core/routes.types';
import {
    Container,
    CardContent,
    Line,
    BottomBar,
    ErrorContainer,
    TermsAndConditions,
    IconsContainer,
    Privacy,
    CheckboxContainer,
} from './StripeForm.style';
import { useStripeFormVM } from './useStripeFormVM';
import { ReactComponent as MasterCard } from './masterCard.svg';
import { ReactComponent as Visa } from './visa.svg';
import { ReactComponent as Lock } from './lock.svg';
import { ReactComponent as Ssl } from './ssl.svg';

type Props = {
    handleStripePaymentSubmit: (stripePaymentMethodId: string | undefined) => void;
    isLoading?: boolean;
    error?: string;
};

const StripeForm: React.FunctionComponent<Props> = ({
    handleStripePaymentSubmit,
    isLoading,
    error,
}) => {
    const {
        termsAndConditions,
        canSubmit,
        options,
        numberStatus,
        expiryStatus,
        cvcStatus,
        handleChange,
        handleSubmit,
        handleTermsAndConditionsChange,
    } = useStripeFormVM(isLoading);

    return (
        <Container onSubmit={handleSubmit(handleStripePaymentSubmit)}>
            <CardContent>
                <Title text="card_information" weight="bold" />
                <Line>
                    <InputWrapper
                        error={numberStatus?.error || ''}
                        isTouched={numberStatus?.complete}
                        disabled={isLoading}
                    >
                        <CardNumberElement options={options} onChange={handleChange('number')} />
                    </InputWrapper>
                </Line>
                <Line>
                    <InputWrapper
                        error={expiryStatus?.error || ''}
                        isTouched={expiryStatus?.complete}
                        disabled={isLoading}
                    >
                        <CardExpiryElement options={options} onChange={handleChange('expiry')} />
                    </InputWrapper>
                    <InputWrapper
                        error={cvcStatus?.error || ''}
                        isTouched={cvcStatus?.complete}
                        disabled={isLoading}
                    >
                        <CardCvcElement options={options} onChange={handleChange('cvc')} />
                    </InputWrapper>
                </Line>
                <TermsAndConditions>
                    <CheckboxContainer onClick={handleTermsAndConditionsChange}>
                        <Checkbox checked={termsAndConditions} />
                    </CheckboxContainer>
                    <div>
                        <Text text="accept_terms_and_conditions" type="small" />{' '}
                        <Link to={Routes.TermsAndConditions}>
                            <Text text="terms_and_conditions_short" type="small" />
                        </Link>
                    </div>
                </TermsAndConditions>
            </CardContent>
            <BottomBar>
                <ErrorContainer>
                    <ErrorMessage>{error}</ErrorMessage>
                </ErrorContainer>
                <Privacy>
                    <Text text="crypted_payment_data" type="small" />
                    <IconsContainer>
                        <MasterCard />
                        <Visa />
                        <Lock />
                        <Ssl />
                    </IconsContainer>
                </Privacy>
                <Button fullWidth disabled={!canSubmit} isSubmit isLoading={isLoading}>
                    <Text text="confirm_and_pay" />
                </Button>
            </BottomBar>
        </Container>
    );
};

export default StripeForm;
